/// <amd-module name="TFS/CreateProject/Components/CreateProjectComponent" />
import { Fabric } from "OfficeFabric/Fabric";
import * as React from "react";
import { ActionCreator } from "TFS/CreateProject/ActionCreator";
import { ActionsHub } from "TFS/CreateProject/ActionsHub";
import { AdvancedOptions } from "TFS/CreateProject/Components/AdvancedOptions";
import { CodeOfConductStatement } from "TFS/CreateProject/Components/CodeOfConductStatement";
import { CollectionNameTextField } from "TFS/CreateProject/Components/CollectionNameTextField";
import { FooterContent } from "TFS/CreateProject/Components/FooterContent";
import { ProjectDescriptionTextField } from "TFS/CreateProject/Components/ProjectDescriptionTextField";
import { ProjectLimitPanel } from "TFS/CreateProject/Components/ProjectLimitPanel";
import { ProjectNameTextField } from "TFS/CreateProject/Components/ProjectNameTextField";
import { ProjectVisibilityOptions } from "TFS/CreateProject/Components/ProjectVisibilityOptions";
import { StatusMessageOverlay } from "TFS/CreateProject/Components/StatusMessageOverlay";
import { StatusMessageCard } from "TFS/CreateProject/Components/StatusMessageCard";
import { Constants, ProjectVisibilityConstants } from "TFS/CreateProject/Constants";
import { INewProjectParameters, StatusMessageType, StatusValueType } from "TFS/CreateProject/Contracts";
import { ICreateProjectComponentState } from "TFS/CreateProject/Models";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import { StoreListener } from "TFS/CreateProject/Shared/StoreListener";
import { StoresHub } from "TFS/CreateProject/StoresHub";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Spinner } from "VSSUI/Spinner";
import { KeyCode } from "VSSUI/Util";

export interface ICreateProjectComponentProps {
    /**
     * Source module from where create project panel is being invoked. Used for telemetry.
     */
    invokingSource: string;
    /**
     *  Optional handler to be attached to the cancel button of the project creation panel
     */
    onCancel?: () => void;
    /**
     * Optional handler to perform operations on scenario complete
     */
    onScenarioComplete?: (status: string) => void;
    /**
     * Optional handler to perform operations on create project complete
     */
    onCreateProjectComplete?: (newProject: string) => void;
    /**
     * Optional: default version control type
     */
    versionControlType?: string;
    /**
     * Optional: default process template
     */
    processTemplate?: string;
    /**
     * Optional: project visibility option (i.e., Everyone/TeamMembers/EveryoneInTenant)
     */
    projectVisibility?: string;
    /**
     * If this is a new collection that has a pre-created project for us to rename, this represents the id of the pre-created project.
     */
    preCreatedProjectId?: string;
    /**
     * Optional: renders light weight panel view with only project name and visibility options.
     */
    isLightWeightView?: boolean;
    /**
     * Optional: acquisition id which will be added in CI events to trace back.
     */
    acquisitionId?: boolean;
    /**
     * Optional: product that will be used to redirect
     */
    product?: string;
    /**
     * Optional: List of ids of the features to enable when creating the project (i.e., "ms.vss-build.pipelines, ms.vss-work.agile", etc.). All features are enabled if omitted.
     */
    defaultFeatureIds?: string[];
    /**
     * Optional CreateProject actions hub
     */
    actionsHub?: ActionsHub;
    /**
     * Optional CreateProject stores hub
     */
    storesHub?: StoresHub;
    /**
     * Optional action creator
     */
    actionCreator?: ActionCreator;
    /**
     * Class name to append to the root element
     */
    className?: string;
    /**
     * Optional skip redirect to project details flag;
     */
    skipRedirectToDetails?: boolean;
}

export class CreateProjectComponent extends VssComponent<ICreateProjectComponentProps, ICreateProjectComponentState> {
    private _projectNameTextField: React.RefObject<ProjectNameTextField> = React.createRef();
    private _actionCreator: ActionCreator;
    private _storesHub: StoresHub;
    private _storeListener: StoreListener;
    private _isHosted: boolean;
    private _hostName: string;
    private _isScenarioRecorded: boolean;
    private _shouldSetFocus: boolean = true;

    constructor(props: ICreateProjectComponentProps, context: IVssComponentContext) {
        super(props, context);

        const pageData = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData();
        this._isHosted = pageData ? pageData.isHosted : false;
        this._hostName = pageData ? pageData.hostName : "";
        const actionsHub = props.actionsHub ? props.actionsHub : new ActionsHub();
        this._storesHub = new StoresHub(
            actionsHub,
            props.invokingSource,
            props.versionControlType,
            props.processTemplate,
            props.projectVisibility,
            props.defaultFeatureIds
        );
        this._actionCreator = props.actionCreator
            ? props.actionCreator
            : new ActionCreator(actionsHub, this.context.pageContext, undefined, undefined, undefined, this.props.onCreateProjectComplete);

        // Initialize the component
        this._actionCreator.fetchProjectCreationMetadata();
        this._actionCreator.fetchExistingProjectNames();
        this._actionCreator.checkProjectLimit();
        this.state = this._storesHub.store.state;
    }

    public render(): JSX.Element {
        const disabled = !this._canEnableFormElement();
        const isLightWeightView = this.props.isLightWeightView;
        const {
            projectNameState,
            projectParamsState,
            statusMessage,
            pageStatus,
            creationStatus,
            projectCreationMetadata,
            projectLimitState,
            showAdvancedOptions
        } = this.state;
        const showLoadingSpinner = pageStatus.value === StatusValueType.InProgress;
        const showPolicyMessage = this._showPolicyMessage();
        const showReportingWarningMessage = projectCreationMetadata.isReportingConfigured;
        const showCodeOfConductStatement =
            this._showProjectVisibilityOptions() && projectParamsState.currentProjectVisibilityOption.id === ProjectVisibilityConstants.Everyone;
        const showProjectLimitPanel = this._showProjectlimitMessage();

        return showLoadingSpinner ? (
            <Spinner className="flex-grow flex-center" label={CreateProjectResources.LoadingText} />
        ) : (
            <Fabric className={css("create-project-component flex-column", this.props.className)}>
                <fieldset className="create-project-component-fieldset">
                    <div aria-live="assertive" aria-atomic="true">
                        {isFeatureFlagEnabled(this.context.pageContext, Constants.NewCreateProjectPanelErrorMessageCard) ? (
                            <StatusMessageCard statusMessage={statusMessage} />
                        ) : (
                            <StatusMessageOverlay statusMessage={statusMessage} onCtaClick={this._onStatusMessageCtaClick} />
                        )}
                    </div>
                    {!this._isHosted && <CollectionNameTextField hostName={this._hostName} />}

                    {showProjectLimitPanel && <ProjectLimitPanel status={projectLimitState.status} />}
                    <ProjectNameTextField
                        value={projectNameState.name}
                        projectNameStatus={projectNameState.status.value}
                        projectCreationStatus={creationStatus.value}
                        disabled={disabled}
                        onInputChange={this._onInputChange}
                        onProjectNameTextFieldKeyDown={this._onProjectNameTextFieldKeyDown}
                        errorMessage={projectNameState.status.message}
                        ref={this._projectNameTextField}
                    />
                    {!isLightWeightView && (
                        <ProjectDescriptionTextField
                            onProjectDescriptionTextFieldKeyDown={this._onProjectDescriptionTextFieldKeyDown}
                            onChanged={this._actionCreator.updateProjectDescription}
                            value={projectParamsState.projectDescription}
                            disabled={disabled}
                        />
                    )}
                    {this._showProjectVisibilityOptions() && (
                        <ProjectVisibilityOptions
                            disabled={disabled}
                            projectVisibilityOptions={projectCreationMetadata.projectVisibilityOptions}
                            currentProjectVisibilityOption={projectParamsState.currentProjectVisibilityOption}
                            onProjectVisibilityChanged={this._actionCreator.updateProjectVisibilityOption}
                        />
                    )}
                    {showPolicyMessage && (
                        <div className="form-section">
                            <FormatComponent format={CreateProjectResources.CreateProjectPolicyMessage}>
                                <Link
                                    className="link-text"
                                    href={routeUrl(this.context.pageContext, "ms.vss-admin-web.collection-admin-hub-route", {
                                        adminPivot: "organizationPolicy"
                                    })}
                                >
                                    {CreateProjectResources.CreateProjectPolicyLinkText}
                                </Link>
                            </FormatComponent>
                        </div>
                    )}
                    {showCodeOfConductStatement && <CodeOfConductStatement pageContext={this.context.pageContext} />}
                    {showReportingWarningMessage && (
                        <div className="form-section flex-row">
                            <Icon className="reporting-warning-icon fontSizeM" iconName={"Warning"} />
                            <FormatComponent format={CreateProjectResources.ReportingServiceWarningMessage}>
                                <Link
                                    className="link-text"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={CreateProjectResources.ReportingServiceWarningUrl}
                                >
                                    {CreateProjectResources.FindOutText}
                                </Link>
                            </FormatComponent>
                        </div>
                    )}
                    {!isLightWeightView && (
                        <AdvancedOptions
                            showAdvancedOptions={showAdvancedOptions}
                            disabled={disabled}
                            onAdvancedOptionClick={this._toggleAdvancedOptionsVisibility}
                            currentVersionControlType={projectParamsState.currentVersionControlType}
                            currentProcessTemplate={projectParamsState.currentProcessTemplateName}
                            onVersionControlComboOptionChanged={this._actionCreator.updateVersionControlType}
                            onProcessTemplateComboOptionChanged={this._actionCreator.updateProcessTemplate}
                        />
                    )}
                </fieldset>
                <FooterContent
                    pageStatus={this.state.pageStatus.value}
                    onCreateButtonClick={this._onCreateButtonClick}
                    onCancelButtonClick={this.props.onCancel ? this._onCancelButtonClick : undefined}
                    isCreateButtonDisabled={!this._canEnableCreateProjectCta()}
                    isCancelButtonDisabled={!this._canEnableFormElement()}
                />
            </Fabric>
        );
    }

    public componentDidUpdate(prevProps: ICreateProjectComponentProps, prevState: ICreateProjectComponentState): void {
        super.componentDidUpdate(prevProps, prevState);
        // If the current state has the navigation url, navigate to that url
        if (this.state.navigationUrl) {
            window.location.href = this.state.navigationUrl;
            return;
        }

        if (this._shouldSetFocus && this.state.projectCreationMetadata.canUserCreateProject && this._projectNameTextField) {
            this._shouldSetFocus = false;
            if (this._projectNameTextField.current) {
                this._projectNameTextField.current.focus();
            }
            // focus
            // focusAsync(this._projectNameTextField);
        }

        // Call the scenario complete callback once page data is loaded and rendered
        const pageStatus = this.state.pageStatus.value;
        if (
            !this._isScenarioRecorded &&
            pageStatus !== StatusValueType.InProgress &&
            pageStatus !== StatusValueType.NoStatus &&
            this.props.onScenarioComplete
        ) {
            const status = pageStatus === StatusValueType.Failure ? "Failed" : "Succeeded";
            this.props.onScenarioComplete(status);
            this._isScenarioRecorded = true;
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.storeListener.addChangedListener(this._storesHub.store, this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        if (this._storeListener) {
            this._storeListener.disposeAllListeners();
        }

        if (this._storesHub) {
            this._storesHub.dispose();
        }
    }

    // TODO: Remove this method once we have migrated to new status message card (Task 2116828)
    private _onStatusMessageCtaClick = (): void => {
        const statusMessage = this.state.statusMessage;
        const statusMessageType = statusMessage.statusMessageType;
        const creationStatusType = this.state.creationStatus.value;

        if (statusMessage.showGoBackCta) {
            // Need to edit form values, so dismiss status message
            this._actionCreator.dismissStatusMessage();
            if (this._projectNameTextField.current) {
                this._projectNameTextField !== null && this._projectNameTextField.current.focus();
            }
            // this._projectNameTextField !== null && focusAsync(this._projectNameTextField);
        } else if (statusMessage.showTryAgainCta && creationStatusType === StatusValueType.Failure) {
            // Project creation has failed, so try again creating the project
            this._onCreateButtonClick();
        } else if (statusMessage.showTryAgainCta && statusMessageType === StatusMessageType.Reload) {
            // Initial data load has failed, so try again loading the panel
            this._actionCreator.reloadPanel();
        }
    };

    private _toggleAdvancedOptionsVisibility = (): void => {
        this._actionCreator.toggleAdvancedOptionsVisibility();

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, "CreateProjectPanel.AdvancedOptionsClicked", {
            advancedOptionsShownOnClick: !this.state.showAdvancedOptions
        });
    };

    private _onStoreChanged = (): void => {
        this.setState(this._storesHub.store.state);
    };

    /**
     * Keydown event handler for project name text field to decide if the project creation is to be initiated
     */
    private _onProjectNameTextFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.keyCode === KeyCode.enter && !event.shiftKey && this._canEnableCreateProjectCta()) {
            this._onCreateButtonClick();
        }
    };

    /**
     * Keydown event handler for project description text field to decide if the project creation is to be initiated
     */
    private _onProjectDescriptionTextFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.keyCode === KeyCode.enter && event.ctrlKey && this._canEnableCreateProjectCta()) {
            this._onCreateButtonClick();
        }
    };

    /**
     * Click handler for the Create button
     */
    private _onCreateButtonClick = (): void => {
        // Invoke the create project function to trigger the project creation
        this._createProject();

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, "CreateProjectPanel.CreateButtonClicked", {
            source: this.state.source,
            acquisitionId: this.props.acquisitionId,
            product: this.props.product,
            featureIds: this.state.featureIds,
            projectName: this.state.projectNameState.name,
            isPreCreatedProject: !!this.props.preCreatedProjectId
        });
    };

    /**
     * Click handler for the Cancel button
     */
    private _onCancelButtonClick = (): void => {
        this._cancelCreateProject("CancelButton");
    };

    /**
     * Click handler for the Cancel/Dismiss button. It unmounts create project panel.
     * If cancelled after clicking create project button then project creation will continue on server.
     */
    private _cancelCreateProject = (ctaSource: string): void => {
        const creationStatus = this.state.creationStatus.value;
        const waitingForProjectCreation = creationStatus === StatusValueType.InProgress || creationStatus === StatusValueType.Success; // Need StatusValueType.Success as well because we show spinner while navigating once project created

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, "CreateProjectPanel.CancelButtonClicked", {
            source: this.state.source,
            ctaSource: ctaSource,
            waitingForProjectCreation: waitingForProjectCreation
        });

        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    /**
     * Invokes the new team project creation flow with the entered /selected parameters
     */
    private _createProject(): void {
        const projectNameState = this.state.projectNameState;
        const projectParamsState = this.state.projectParamsState;
        let textFieldValue = "";
        if (this._projectNameTextField.current && this._projectNameTextField.current.props.value) {
            textFieldValue = this._projectNameTextField.current.props.value.toString();
        }
        const newProjectParams: INewProjectParameters = {
            product: this.props.product,
            featureIds: this.state.featureIds,
            projectName: projectNameState.name.trim(),
            projectDescription: projectParamsState.projectDescription.trim(),
            processTemplateTypeId: this._shouldUseDefaultTemplate() ? Constants.DefafultProcessTemplate : projectParamsState.currentProcessTemplateId,
            versionControlOption: projectParamsState.currentVersionControlType,
            projectVisibilityOption:
                projectParamsState.currentProjectVisibilityOption != undefined ? projectParamsState.currentProjectVisibilityOption.id : undefined
        };

        // Invoking the project creation call
        this._actionCreator.createProject(
            newProjectParams,
            textFieldValue,
            this.state.existingProjectNames,
            this.props.preCreatedProjectId,
            this.props.skipRedirectToDetails
        );
    }

    private _shouldUseDefaultTemplate(): boolean {
        return (
            isFeatureFlagEnabled(this.context.pageContext, "Azure.DevOps.Acquisition.EnableDefaultProcessForProjectCreate") &&
            this.props.isLightWeightView === true
        );
    }

    /**
     * Returns whether the project visibility options are to be displayed or not
     */
    private _showProjectVisibilityOptions(): boolean {
        return (
            this.state.projectParamsState.projectVisibilityOptions != undefined && this.state.projectParamsState.projectVisibilityOptions.length > 0
        );
    }

    /**
     * Returns whether the policy message should be shown
     */
    private _showPolicyMessage(): boolean {
        return (
            this.state.projectCreationMetadata &&
            this.state.projectCreationMetadata.projectVisibilityOptions &&
            this.state.projectCreationMetadata.projectVisibilityOptions.filter((o) => o.id === ProjectVisibilityConstants.Everyone && o.isDisabled)
                .length > 0
        );
    }

    /**
     * Returns whether the form element is to be enabled or not based on user permission state
     */
    private _canEnableFormElement(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, Constants.NewCreateProjectPanelErrorMessageCard) ? (
            this.state.projectCreationMetadata != undefined
        ) : (
            this.state.projectCreationMetadata != undefined && this.state.statusMessage.statusMessageType === StatusMessageType.NoStatus // Disable components if statusMessage is present;
        )
    }
    
    /**
     * Returns whether the Create project CTA button should be enabled or not based on the state
     */
    private _canEnableCreateProjectCta(): boolean {
        if (
            !this._canEnableFormElement() ||
            !this.state.projectNameState.name ||
            this.state.projectNameState.status.value !== StatusValueType.Success ||
            (this.state.projectLimitState.status.value !== StatusValueType.Success && this._actionCreator.getProjectLimitFeatureFlag())
        ) {
            return false;
        }

        return true;
    }

    /**
     * Returns whether the policy message should be shown
     */
    private _showProjectlimitMessage(): boolean {
        const disableProjectLimitFeatureFlagState = this._actionCreator.getProjectLimitFeatureFlag();
        if (!disableProjectLimitFeatureFlagState) {
            // First check that project blocking feature is enabled
            if (!this.state.projectLimitState.isBelowWarning || !this.state.projectLimitState.isBelowLimit) {
                return true;
            }
        }

        return false;
    }

    /**
     * To check the validity of the project name entered in the project name text box
     * @param newValue - The new value entered in the project name text box
     */
    private _onInputChange = (newValue: string): string => {
        if (this._canEnableFormElement()) {
            if (newValue) {
                this._actionCreator.validateAndUpdateProjectName(newValue, this.state.existingProjectNames);
            } else if (this.state.projectNameState.status.value !== StatusValueType.NoStatus) {
                // If the status is already reset, do not call reset again
                // This is added to prevent validation check during mount
                this._actionCreator.resetProjectNameValidation();
            }
        }
        return "";
    };

    private get storeListener(): StoreListener {
        if (!this._storeListener) {
            this._storeListener = new StoreListener();
        }
        return this._storeListener;
    }
}

VssComponent.register("createProjectComponent", CreateProjectComponent);
